import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  applicationsV2, processorEmails, CLAIMABLE,
  type ApplicationV2ListItem, type ProcessorEmailItem, type ProcessorEmailList,
} from "@/features/applications/api/applications.api";
import { StatusPill, APPLICATION_STATUS_TONES, APPLICATION_STATUS_LABELS } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Notice } from "@/components/Notice";
import { Loading } from "@/components/Loading";
import { Dash } from "@/components/Dash";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toastApiError } from "@/lib/toast";
import { findMergeCandidates, MergeWithDialog, type MergeCandidate } from "@/features/applications/components/DuplicatesPanel";
import { formatDateTime } from "@/utils/dates";

const KIND_LABELS: Record<ProcessorEmailItem["kind"], string> = {
  Unknown: "Unrecognized",
  ESigned: "E-signed",
  ActionNeeded: "Pended",
  Approved: "Approved",
  CodingComplete: "Coding complete",
  FirstDeposit: "First deposit",
  UnderwritingReply: "Underwriting reply",
  UnderwritingSent: "Sent to underwriting",
};

const RESULT: Record<ProcessorEmailItem["result"], { label: string; tone: "success" | "muted" | "warning" | "danger" }> = {
  Applied: { label: "Applied", tone: "success" },
  Ignored: { label: "Ignored", tone: "muted" },
  NeedsPerson: { label: "Needs a person", tone: "warning" },
  Flagged: { label: "Applied · check it", tone: "danger" },
  Claimed: { label: "Claimed", tone: "success" },
  Resolved: { label: "Checked", tone: "muted" },
};

const RESULT_TONES = Object.fromEntries(Object.entries(RESULT).map(([k, v]) => [k, v.tone]));
const RESULT_LABELS = Object.fromEntries(Object.entries(RESULT).map(([k, v]) => [k, v.label]));

export function ProcessorEmailsPanel({ onCount }: { onCount?: (n: number) => void }) {
  const [attentionOnly, setAttentionOnly] = useState(true);
  const [data, setData] = useState<ProcessorEmailList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState<ProcessorEmailItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [mergePrompt, setMergePrompt] = useState<{ id: string; name: string | null; agentName: string | null; others: MergeCandidate[]; what: string } | null>(null);

  const latest = useRef(0);
  const load = useCallback(() => {
    const request = ++latest.current;
    setError(null);
    processorEmails.list(attentionOnly)
      .then((d) => { if (request === latest.current) { setData(d); onCount?.(d.needsAttention); } })
      .catch((e) => { if (request === latest.current) setError(e instanceof Error ? e.message : "Couldn't load TSYS updates."); });
  }, [attentionOnly, onCount]);

  useEffect(() => { load(); }, [load]);

  async function offerMerge(appId: string, name: string | null, what: string) {
    const { self, others } = await findMergeCandidates(appId, name);
    if (others.length > 0) setMergePrompt({ id: appId, name: self?.name ?? name, agentName: self?.agentName ?? null, others, what });
  }

  async function linkSuggested(e: ProcessorEmailItem) {
    if (!e.suggestion) return;
    setBusy(true);
    try {
      await processorEmails.claim(e.id, e.suggestion.applicationId);
      toast.success(`Linked to ${e.suggestion.name ?? "the application"}. Its status is updated.`);
      load();
      void offerMerge(e.suggestion.applicationId, e.suggestion.name, linkedWhat(e));
    } catch (err) {
      toastApiError(err, "Couldn't link this email.");
    } finally {
      setBusy(false);
    }
  }

  async function resolve(e: ProcessorEmailItem) {
    setBusy(true);
    try {
      await processorEmails.resolve(e.id);
      toast.success("Marked checked.");
      load();
    } catch (err) {
      toastApiError(err, "Couldn't mark it checked.");
    } finally {
      setBusy(false);
    }
  }

  if (error) return <Notice tone="error">{error}</Notice>;
  if (!data) return <Loading>Loading TSYS updates…</Loading>;

  return (
    <div className="space-y-3">
      <Tabs value={attentionOnly ? "attention" : "all"} onValueChange={(v) => setAttentionOnly(v === "attention")}>
        <TabsList>
          <TabsTrigger value="attention">Needs a person ({data.needsAttention})</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>
      {data.page.items.length === 0 ? (
        <Notice tone="info">
          {attentionOnly ? "Nothing needs a person right now." : "No TSYS updates yet."}
        </Notice>
      ) : (
        <div className="rounded-md border bg-white shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Received</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Business · MID</TableHead>
                <TableHead>Result</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.page.items.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="whitespace-nowrap text-xs text-slate-600">{formatDateTime(e.receivedAt)}</TableCell>
                  <TableCell>
                    <div className="font-medium text-slate-800">{KIND_LABELS[e.kind]}</div>
                    {e.additionalInfo && <div className="max-w-md text-xs text-slate-500">{e.additionalInfo}</div>}
                    {(e.fileCount ?? 0) > 0 && (
                      <div className="text-xs text-slate-500">
                        📎 {e.fileCount} {e.fileCount === 1 ? "file" : "files"} · added to the application when you claim it
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-slate-800">{e.dbaName ?? <Dash />}</div>
                    <div className="font-mono text-xs text-slate-500">{e.mid ?? "—"}</div>
                  </TableCell>
                  <TableCell>
                    <StatusPill value={e.result} map={RESULT_TONES} labels={RESULT_LABELS} />
                    {e.applicationId && (
                      <div className="mt-1 text-xs">
                        → <Link to={`/applications/v2/${e.applicationId}`} className="text-slate-700 hover:underline">{e.applicationName ?? "Open application"}</Link>
                      </div>
                    )}
                    {e.note && !(e.result === "NeedsPerson" && e.suggestion) && <div className="mt-1 max-w-xs text-xs text-slate-500">{e.note}</div>}
                    {e.result === "NeedsPerson" && e.suggestion && (
                      <div className="mt-2 max-w-xs rounded-md bg-slate-50 px-3 py-2">
                        <div className="text-xs text-slate-500">Looks like</div>
                        <Link to={`/applications/v2/${e.suggestion.applicationId}`} className="font-medium text-slate-800 hover:underline">
                          {e.suggestion.name ?? "Untitled application"}
                        </Link>
                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                          {e.suggestion.ownerName && <span>{e.suggestion.ownerName} ·</span>}
                          <StatusPill value={e.suggestion.status} map={APPLICATION_STATUS_TONES} labels={APPLICATION_STATUS_LABELS} />
                        </div>
                      </div>
                    )}
                    {e.resolvedBy && <div className="mt-1 text-xs text-slate-400">{e.resolvedBy} · {formatDateTime(e.resolvedAt)}</div>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <div className="flex flex-col items-end gap-2">
                      {e.result === "NeedsPerson" && e.suggestion && (
                        <Button type="button" size="sm" onClick={() => linkSuggested(e)} disabled={busy}>Link and update</Button>
                      )}
                      {e.result === "NeedsPerson" && claimable(e) && (
                        <Button type="button" size="sm" variant={e.suggestion ? "outline" : "default"} onClick={() => setClaiming(e)} disabled={busy}>
                          {e.suggestion ? "Pick another" : "Pick application"}
                        </Button>
                      )}
                      {(e.result === "Flagged" || (e.result === "NeedsPerson" && !claimable(e))) && (
                        <Button type="button" size="sm" variant="outline" onClick={() => resolve(e)} disabled={busy}>
                          Mark checked
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {claiming && (
        <ClaimDialog
          email={claiming}
          onClose={() => setClaiming(null)}
          onDone={(appId, name) => { const what = linkedWhat(claiming); setClaiming(null); load(); void offerMerge(appId, name, what); }}
          onNoneOfThese={() => { const e = claiming; setClaiming(null); void resolve(e); }}
        />
      )}
      {mergePrompt && (
        <MergeWithDialog
          app={{ id: mergePrompt.id, name: mergePrompt.name }}
          agentName={mergePrompt.agentName}
          initial={mergePrompt.others}
          linked
          linkedWhat={mergePrompt.what}
          onClose={() => setMergePrompt(null)}
          onMerged={() => { setMergePrompt(null); load(); }}
        />
      )}
    </div>
  );
}

function ClaimDialog({ email, onClose, onDone, onNoneOfThese }: {
  email: ProcessorEmailItem;
  onClose: () => void;
  onDone: (appId: string, name: string | null) => void;
  onNoneOfThese: () => void;
}) {
  const [keyword, setKeyword] = useState(searchName(email.dbaName));
  const [options, setOptions] = useState<ApplicationV2ListItem[] | null>(null);
  const [showingAll, setShowingAll] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const isReply = email.kind === "UnderwritingReply";

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      applicationsV2.list({ pageSize: 20, keyword, status: CLAIMABLE })
        .then(async (r) => {
          if (r.items.length > 0 || !keyword.trim()) {
            if (!cancelled) { setOptions(r.items); setShowingAll(!keyword.trim()); }
            return;
          }
          const all = await applicationsV2.list({ pageSize: 50, keyword: "", status: CLAIMABLE });
          if (!cancelled) { setOptions(all.items); setShowingAll(true); }
        })
        .catch(() => { if (!cancelled) setOptions([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [keyword]);

  async function claim() {
    if (!picked) return;
    setSaving(true);
    try {
      await processorEmails.claim(email.id, picked);
      toast.success(isReply ? "Posted to the application's underwriting chat." : "Claimed. Future emails for this MID match automatically.");
      const app = options?.find((o) => o.id === picked);
      onDone(picked, app ? app.dbaName ?? app.legalName : null);
    } catch (err) {
      toastApiError(err, "Couldn't claim this email.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Which application is this?</DialogTitle>
          <DialogDescription>
            {[email.dbaName, email.mid && `MID ${email.mid}`, KIND_LABELS[email.kind]].filter(Boolean).join(" · ")}. {email.note}
          </DialogDescription>
        </DialogHeader>
        <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Search business name…" aria-label="Search applications" />
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {options === null ? (
            <Loading>Searching…</Loading>
          ) : options.length === 0 ? (
            <p className="text-sm text-slate-500">No open applications.</p>
          ) : (<>
            {showingAll && keyword.trim() && (
              <p className="text-xs text-slate-500">No name matches "{keyword}". Showing all open applications.</p>
            )}
            {options.map((a) => (
            <label key={a.id} className="flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm hover:bg-slate-50">
              <input type="radio" name="claim-app" checked={picked === a.id} onChange={() => setPicked(a.id)} />
              <span className="flex-1">
                <span className="font-medium text-slate-800">{a.dbaName || a.legalName}</span>
                <span className="block text-xs text-slate-500">
                  {a.owner?.name ?? "No owner"}{a.processorMid ? ` · MID ${a.processorMid}` : ""}
                </span>
              </span>
              <StatusPill value={a.status} map={APPLICATION_STATUS_TONES} labels={APPLICATION_STATUS_LABELS} />
            </label>
            ))}
          </>)}
        </div>
        <p className="text-xs text-slate-500">
          {isReply
            ? "Claiming posts this reply into the application's underwriting chat."
            : "Claiming saves the MID on the application, catches its status up, and future emails for this MID match automatically."}
        </p>
        <DialogFooter>
          <Button type="button" variant="ghost" className="sm:mr-auto" onClick={onNoneOfThese} disabled={saving}>None of these, mark checked</Button>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" onClick={claim} disabled={!picked || saving}>{saving ? "Claiming…" : "Claim"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function searchName(name: string | null | undefined): string {
  return (name ?? "")
    .replace(/[\s,.]+$/, "")
    .replace(/\s+(?:(?:#|no\.|location|loc\.?)\s*\d{1,3}|#?(?:[1-9]|1[0-9]|20))$/i, "")
    .replace(/[\s,]+(llc|inc|corp|co|ltd)\.?$/i, "")
    .replace(/[\s,.]+$/, "")
    .trim();
}

function claimable(e: ProcessorEmailItem): boolean {
  return e.kind === "UnderwritingReply" || (!!e.mid && e.kind !== "Unknown");
}

function linkedWhat(e: ProcessorEmailItem) {
  return e.kind === "UnderwritingReply" ? "underwriting reply" : "TSYS update";
}
